import React from 'react';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';

function PrivacyPolicyPage() {
  return (
    <>
      <SEOHead {...seoConfigs.privacy} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Header */}
        <div className="bg-[#e4fde1] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-[#254e70]" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
              Privacy Policy
            </h1>
            <p className="text-2xl text-[#254e70]/80 mb-4 max-w-4xl mx-auto">
              Your privacy and data security are our top priorities
            </p>
            <p className="text-lg text-[#254e70]/60">
              Last updated: February 1, 2025
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
              <div className="prose prose-lg max-w-none">

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    1. Introduction
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    TherapyList ("we," "our," or "us") operates the TherapyList website and platform (the "Service"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy describes how we collect, use, and share information when you use our Service.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    This policy applies to all information collected through our Service and any related services, sales, marketing, or events. Please read this privacy policy carefully as it will help you understand what we do with the information that we collect.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    2. Information We Collect
                  </h2>
                  
                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Personal Information You Provide</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    We collect personal information that you voluntarily provide when you:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Register for an account</li>
                    <li>Contact therapists through our platform</li>
                    <li>Fill out forms or surveys</li>
                    <li>Contact us for customer support</li>
                    <li>Subscribe to newsletters or marketing communications</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    This information may include: name, email address, phone number, location, insurance information, mental health needs or preferences, and any other information you choose to provide.
                  </p>

                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Automatically Collected Information</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    When you visit our Service, we automatically collect certain information, including:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Device information (IP address, browser type, operating system)</li>
                    <li>Usage data (pages visited, time spent, search queries)</li>
                    <li>Location data (if enabled)</li>
                    <li>Cookies and similar technologies</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Information from Third Parties</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We may receive information from therapists, insurance providers, or other third parties to verify credentials or facilitate connections, but only with appropriate consent and authorization.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    3. How We Use Your Information
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    We use the information we collect for legitimate business purposes, including:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Providing and maintaining our Service</li>
                    <li>Facilitating connections between clients and therapists</li>
                    <li>Processing inquiries and communications</li>
                    <li>Improving our platform and user experience</li>
                    <li>Sending relevant updates and notifications</li>
                    <li>Ensuring compliance with legal obligations</li>
                    <li>Protecting against fraud and security threats</li>
                    <li>Analyzing usage patterns to improve our services</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    4. Information Sharing and Disclosure
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                  </p>
                  
                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">With Therapists</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    When you contact a therapist through our platform, we share relevant information to facilitate the connection, including your contact information and any details you've provided about your needs.
                  </p>

                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Service Providers</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We may share information with trusted third-party service providers who assist us in operating our platform, such as hosting, analytics, customer support, and payment processing.
                  </p>

                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Legal Requirements</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We may disclose information if required by law, court order, or to protect the rights, property, or safety of TherapyList, our users, or others.
                  </p>

                  <h3 className="text-xl font-semibold text-[#254e70] mb-3 mt-6">Business Transfers</h3>
                  <p className="text-gray-700 leading-relaxed">
                    In the event of a merger, acquisition, or sale of assets, user information may be transferred as part of the transaction, subject to the same privacy protections.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    5. Data Security
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security audits and assessments</li>
                    <li>Access controls and authentication</li>
                    <li>Secure hosting and data storage</li>
                    <li>Employee training on data protection</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    6. HIPAA Compliance
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    While TherapyList facilitates connections between clients and therapists, we are not a covered entity under HIPAA (Health Insurance Portability and Accountability Act). However, we recognize the sensitive nature of mental health information and implement privacy practices that align with HIPAA principles.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Once you begin a therapeutic relationship with a provider, your communications and health information with that provider are subject to their HIPAA compliance and privacy practices.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    7. Your Privacy Rights
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Depending on your location, you may have the following rights regarding your personal information:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Right to access your personal information</li>
                    <li>Right to correct inaccurate information</li>
                    <li>Right to delete your personal information</li>
                    <li>Right to restrict processing of your information</li>
                    <li>Right to data portability</li>
                    <li>Right to opt-out of certain data processing</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    8. Cookies and Tracking Technologies
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We use cookies and similar tracking technologies to enhance your experience on our platform. These technologies help us:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>Remember your preferences and settings</li>
                    <li>Analyze website traffic and usage patterns</li>
                    <li>Provide personalized content and recommendations</li>
                    <li>Improve our services and user experience</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed">
                    You can control cookies through your browser settings. However, disabling cookies may affect some functionality of our Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    9. Data Retention
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We retain personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Factors that determine retention periods include:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
                    <li>The nature and sensitivity of the information</li>
                    <li>Legal and regulatory requirements</li>
                    <li>The purposes for which we collected the information</li>
                    <li>Whether the purposes can be achieved through other means</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    10. Children's Privacy
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information promptly.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    11. Changes to This Privacy Policy
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    12. Contact Us
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us at:
                  </p>
                  <div className="bg-[#e4fde1] p-6 rounded-lg">
                    <p className="text-gray-700 mb-2"><strong>TherapyList</strong></p>
                    <p className="text-gray-700">Email: help@therapylist.com</p>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PrivacyPolicyPage; 